import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import _ from 'lodash';

import { Ticket } from '../../../../../api-client';
import { RouterSettings } from '../../../../../utils/router';
import { TRANSLATION_KEYS } from './localization/translation-keys';

import {
    IAIDetailsList,
    IAIContextualMenuColumn,
    IAIDatetimeColumn,
    IAIPreviewColumn,
    IAIPriorityColumn,
    IAIStatusColumn,
    IAIStringColumn
} from '../../../../../common/components/IAITable';
import { useBoolean } from '@fluentui/react-hooks';
import IAIColumnsRefinementDrawer from '../../../../../common/components/IAIColumnsRefinementDrawer/iai-columns-refinements-drawer';
import { ITicketColumn } from '../../../../../common/components/IAITable/IAIDetailsList/iai-details-list';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/application/redux-store';

interface IPersonalTicketsPivot {
    dataSource: Ticket[];
    dataLoading?: boolean;
}

const PersonalTicketsPivot: React.FunctionComponent<IPersonalTicketsPivot> = ({
    dataSource,
    dataLoading
}) => {
    const _handleSearchPredicate = (dataItem: Ticket, searchKey: string) => (
        _.includes(dataItem.id?.toLowerCase(), searchKey) ||
        _.includes(dataItem.subject?.toLowerCase(), searchKey) ||
        _.includes(dataItem.description?.toLowerCase(), searchKey) ||
        _.includes(dataItem.requestedBy?.toLowerCase(), searchKey)
    );

    //#region Form-Management

    const navigate = useNavigate();
    const [columnsRefinementDrawerVisibile, { toggle: toggleColumnsRefinementDrawerVisibile }] = useBoolean(false);

    const _handleCreateAction = () => {
        navigate(RouterSettings.PATHS.dashboard.ticket.edit('00-0000'));
    }

    const _handleViewAction = (dataItem: Ticket) => {
        if (!dataItem.id) {
            return;
        }

        navigate(RouterSettings.PATHS.dashboard.ticket.view(dataItem.id));
    }

    //#endregion

    const {
        t: translate
    } = useTranslation();

    const properties = useSelector((state: RootState) => state.system.customAttributes);
    const [activeColumns, setActiveColumns] = useState<ITicketColumn[]>([]);

    const propertiesColumns = () => {
        return _.map(properties, dataItem => ({
            key: `tck__Property_${dataItem.id}`,
            name: dataItem.displayName,
            minWidth: 100,
            isResizable: true,
            onRender: (dataSourceItem: Ticket) => (
                <IAIStringColumn align="start" text={dataSourceItem?.properties?.find(x => x.id === dataItem.id)?.value} />
            ),
            isActive: dataItem?.defaultRender,
        } as ITicketColumn))
    };

    const dataColumns = useMemo(() => {
        return [
            {
                key: 'tck__preview',
                fieldName: '',
                name: '',
                minWidth: 50,
                maxWidth: 50,
                onRender: (dataItem: Ticket) => (
                    <IAIPreviewColumn
                        heading={dataItem.subject}
                        content={parse(dataItem.description || '')}
                        tooltip={translate(TRANSLATION_KEYS.ColumnPreviewTooltip)}
                    />
                ),
                isLocked: true,
            },
            {
                key: 'tck__subject',
                fieldName: 'subject',
                name: translate(TRANSLATION_KEYS.ColumnTitleLabel),
                minWidth: 200,
                isResizable: true,
                onRender: (dataItem: Ticket) => (
                    <IAIStringColumn align="start" text={dataItem.subject} />
                ),
                isActive: true,
            },
            {
                key: 'tck__createdAt',
                fieldName: 'createdAt',
                name: translate(TRANSLATION_KEYS.ColumnCreatedAtLabel),
                minWidth: 100,
                maxWidth: 100,
                onRender: (dataItem: Ticket) => (
                    <IAIDatetimeColumn align="start" datetime={dataItem.createdAt} />
                ),
                isActive: true,
            },
            {
                key: 'tck__status',
                fieldName: 'status',
                name: translate(TRANSLATION_KEYS.ColumnStatusLabel),
                minWidth: 100,
                maxWidth: 100,
                onRender: (dataItem: Ticket) => (
                    <IAIStatusColumn align="start" status={dataItem.status} />
                ),
                isActive: true,
            },
            {
                key: 'tck__department',
                fieldName: 'department',
                name: translate(TRANSLATION_KEYS.ColumnDepartmentLabel),
                minWidth: 200,
                isResizable: true,
                onRender: (dataItem: Ticket) => (
                    <IAIStringColumn align="start" text={dataItem.department?.displayName} />
                ),
                isActive: true,
            },
            {
                key: 'tck__priority',
                fieldName: 'priority',
                name: translate(TRANSLATION_KEYS.ColumnPriorityLabel),
                minWidth: 100,
                maxWidth: 100,
                onRender: (dataItem: Ticket) => (
                    <IAIPriorityColumn align="start" priority={dataItem.priority} />
                ),
                isActive: true,
            },
            ...propertiesColumns(),
            {
                key: 'tck__actions',
                fieldName: '',
                name: '',
                minWidth: 50,
                maxWidth: 50,
                onRender: (dataItem: Ticket, dataIndex: any) => (
                    <IAIContextualMenuColumn
                        menuItems={[
                            {
                                key: `menuItem__view_${dataIndex}`,
                                iconProps: { iconName: 'View' },
                                text: translate(TRANSLATION_KEYS.ColumnActionCommandViewLabel),
                                title: translate(TRANSLATION_KEYS.ColumnActionCommandViewTooltip),
                                onClick: () => _handleViewAction(dataItem)
                            },
                        ]}
                    />
                ),
                isLocked: true,
            }
        ];
    }, [properties]);

    useEffect(() => {
        setActiveColumns(dataColumns);
    }, [properties]);

    return (
        <div className={`personal-tickets-pivot-container`}>
            <IAIDetailsList
                dataSource={dataSource}
                dataColumns={activeColumns.filter(x => x?.isActive || x?.isLocked)}
                dataLoading={dataLoading}
                commands={[
                    {
                        key: '',
                        disabled: dataLoading,
                        text: translate(TRANSLATION_KEYS.CommandCreateLabel),
                        title: translate(TRANSLATION_KEYS.CommandCreateTooltip),
                        iconProps: { iconName: 'Add' },
                        onClick: _handleCreateAction
                    }
                ]}
                sideCommands={[
                    {
                        key: '',
                        disabled: dataLoading,
                        title: translate(TRANSLATION_KEYS.ColumnTitleLabel),
                        iconProps: { iconName: 'ColumnOptions' },
                        onClick: toggleColumnsRefinementDrawerVisibile
                    },
                ]}
                emptyContentLabel={translate(TRANSLATION_KEYS.DetailsListEmptyContentLabel)}
                onItemInvoked={(dataItem: Ticket) => _handleViewAction(dataItem)}
                onSearchPredicate={_handleSearchPredicate}
            />

            <IAIColumnsRefinementDrawer
                disabled={dataLoading}
                drawerVisible={columnsRefinementDrawerVisibile}
                columns={dataColumns}
                onApplyCriteria={(columns) => {
                    setActiveColumns(columns);
                    toggleColumnsRefinementDrawerVisibile();
                }}
                onDrawerDismiss={toggleColumnsRefinementDrawerVisibile}
            />
        </div>
    );
}

export default PersonalTicketsPivot;